/* eslint-disable import/no-named-as-default */
/* eslint-disable react/jsx-handler-names */
import { Fragment } from 'react';
import PropTypes from 'prop-types';

import { useIsUserLogged, countryToggleConfig } from '@spotahome/ui-library';

import { ListingShape } from '@spotahome/marketplace-common/src/shapes/listing';
import { landlordPoliciesContainFees } from '@spotahome/marketplace-common/src/utils/landlordPolicies';

import { isDateValid } from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';
import ListingTitle from '@spotahome/ui-library/src/tenant/components/ListingTitle';
import PropertyRulesRedesign from '@spotahome/ui-library/src/tenant/components/PropertyRulesRedesign';
import FAQAccordion from '@spotahome/ui-library/src/tenant/components/FAQSection/FAQAccordion';

import { useSoyuzExperiment, trans } from '@spotahome/soyuz/client';

import classNames from 'classnames';

import { getBillsFromFeatures } from '../utils/billsFeaturesAdapter';
import { getRulesFromFeatures } from '../utils/propertyRulesHelper';
import { getFaqs } from '../utils/faqs';

import ListingSectionObservable from './ListingSection/ListingSectionObservable';

import ListingTranslateContent from './ListingTranslateContent';
import ListingFlatmates from './ListingFlatmates';
import ListingHighlights from './ListingHighlights';
import ListingPricing from './ListingPricing';
import ListingAvailabilities from './ListingAvailabilities';
import ListingSurroundings from './ListingSurroundings';
import ListingHowToBook from './ListingHowToBook';
import PropertyRules from './PropertyRules';
import Reviews from './Reviews';
import ExtendedLandlordInformation from './ExtendedLandlordInformation';
import HowBookingWorks from './HowBookingWorks';
import MeetYourLandlord from './MeetYourLandlord';
import OtherRoomsSection from './OtherRoomsSection';
import BillsIncludedModal from './BillsIncludedModal';
import CancellationPolicies from './CancellationPolicies';
import ListingOverview from './ListingOverview';
import ListingOverviewSimplified from './ListingOverviewSimplified';
import PropertyRulesSimplified from './PropertyRulesSimplified';
import ListingValueProposition from './ListingValueProposition';
import ListingDescriptions from './ListingDescriptions';

import ListingLocation from './ListingLocation';
import ListingPaymentsModule from './ListingPaymentsModule/ListingPaymentsModule';

import './ListingInformation.scss';

const SQUARE_METERS_FEATURE_ID = 99999;
const getFeaturesOldModel = listing => {
  const features = {
    ...listing.features,
    ...listing.extraFeatures
  };

  if (listing.m2) {
    const squareMetersFeature = { [SQUARE_METERS_FEATURE_ID]: listing.m2 };
    return { ...features, ...squareMetersFeature };
  }

  return features;
};

const getRoomDescription = (listing, locale) => {
  const room = listing.roomsInApartment.find(item => item.id === listing.id);

  return room &&
    room.contents &&
    room.contents[locale] &&
    room.contents[locale].description
    ? room.contents[locale].description
    : '';
};

const containsPricingTerms = text =>
  /\b(pricing|price|euro|GBP|AED)\b|€|£/i.test(text);

const getRoomDescriptionHasPricing = listing => {
  const room = listing.roomsInApartment.find(item => item.id === listing.id);

  return (
    room?.contents?.en && containsPricingTerms(room.contents.en.description)
  );
};

const getPeriodicCleaningValue = (applyOn, frequency) => {
  if (applyOn === 'included') {
    return 'included';
  }

  if (frequency === 'weekly') {
    return 'availableForAdditionalFeeWeekly';
  }

  if (frequency === 'monthly') {
    return 'availableForAdditionalFee';
  }

  if (frequency === 'biMonthly') {
    return 'availableForAdditionalFeeBimonthly';
  }

  return 'availableForAdditionalFeeOneTime';
};

const getPeriodicCleaningFrequency = (frequency, category) => {
  if (category === 'onCheckIn') {
    return 'onlyAtMoveIn';
  }

  if (category === 'onCheckOut') {
    return 'onlyAtMoveOut';
  }

  return frequency;
};

const getPeriodicCleaningFee = (value, frequency) => {
  const valueInCents = value * 100;

  // Listing cost are transformed to monthly values for weekly and bimonthly frequencies
  // so we need to transform the value back to the original value
  if (frequency === 'weekly') {
    return valueInCents * 0.25;
  }

  if (frequency === 'biMonthly') {
    return valueInCents * 2;
  }

  return valueInCents;
};

const getPeriodicCleaningFromListingCosts = ({
  __typename,
  ...listingCosts
}) => {
  let periodicCleaningItem = null;

  Object.entries(listingCosts).forEach(([category, listingCostGroup]) => {
    listingCostGroup.costs.forEach(cost => {
      if (
        cost.type === 'periodicCleaning' ||
        cost.name === 'periodicCleaning'
      ) {
        periodicCleaningItem = {
          additionalInfo: {
            fee: getPeriodicCleaningFee(cost.value, cost.frequency), // format to cents
            frequency: getPeriodicCleaningFrequency(cost.frequency, category)
          },
          category: 'cleaning',
          value: getPeriodicCleaningValue(cost.applyOn, cost.frequency)
        };
      }
    });
  });
  return periodicCleaningItem;
};

export const ListingInformation = ({
  landlordHomecards = [],
  isRoomsAggregator,
  isWholeApartment,
  locale,
  moveIn = '',
  moveOut = '',
  listing,
  onLanguageChange,
  mustShowLanguageToggle,
  showTranslatedContent,
  deviceType
}) => {
  const isUserLogged = useIsUserLogged();
  const contents = listing.contents[locale];
  const roomDescription = getRoomDescription(listing, locale);
  const hasRoomDescriptionPricing = getRoomDescriptionHasPricing(listing);
  const bills = getBillsFromFeatures(listing.propertyFeatures);
  const landlordRulesInfo = getRulesFromFeatures(listing.propertyFeatures);

  const isListingSimplificationHeaderVariant =
    useSoyuzExperiment('ListingSimplificationHeaderV2').get('behaviour') ===
    'variant';

  const isListingSimplificationBottomVariant =
    useSoyuzExperiment('ListingSimplificationBottom').get('behaviour') ===
    'variant';

  const isListingPaymentsModuleV2Variant =
    useSoyuzExperiment('ListingPaymentsModulev2').get('behaviour') === 'variant';

  const hasDates =
    moveIn && moveOut && isDateValid(moveIn) && isDateValid(moveOut);

  const landlordPoliciesHasFees = landlordPoliciesContainFees(
    contents.landlordPolicies
  );
  const moveLandlordPoliciesToPricing = countryToggleConfig(
    listing.countryCode
  ).moveLandlordPoliciesToPricing;
  const landlordPolicies =
    (!isRoomsAggregator ||
      !moveLandlordPoliciesToPricing ||
      !landlordPoliciesHasFees) &&
    contents.landlordPolicies;

  const classes = classNames('listing-information', {
    'listing-information--simplification-header-variant':
      isListingSimplificationHeaderVariant
  });

  return (
    <Fragment>
      <div className={classes}>
        {mustShowLanguageToggle && (
          <ListingTranslateContent
            isManualTranslated={contents.isManualTranslated}
            isTranslationNeeded={contents.isTranslationNeeded}
            onChangeLanguage={onLanguageChange}
            showTranslatedContent={showTranslatedContent}
          />
        )}

        {isListingSimplificationHeaderVariant && (
          <ListingValueProposition listing={listing} />
        )}

        {(isListingSimplificationHeaderVariant ||
          isListingSimplificationBottomVariant) &&
          (contents.description ||
            contents.landlordDescription ||
            roomDescription ||
            contents.whatWeLiked ||
            contents.thingsToKeepInMind) && (
            <ListingDescriptions
              listing={listing}
              description={contents.description}
              landlordDescription={contents.landlordDescription}
              roomDescription={roomDescription}
              whatWeLiked={contents.whatWeLiked}
              thingsToKeepInMind={contents.thingsToKeepInMind}
              bordered={!isListingSimplificationBottomVariant}
            />
          )}

        {isListingSimplificationBottomVariant && (
          <ListingOverviewSimplified
            listing={listing}
            isRoomsAggregator={isRoomsAggregator}
            isWholeApartment={isWholeApartment}
            listingId={listing.id}
            propertyFeatures={listing.propertyFeatures}
            propertyFeaturesOldModel={getFeaturesOldModel(listing)}
            roomsInApartment={listing.roomsInApartment}
          />
        )}

        {!isListingSimplificationBottomVariant && (
          <ListingOverview
            description={contents.description}
            landlordDescription={contents.landlordDescription}
            details={contents.details}
            isRoomsAggregator={isRoomsAggregator}
            isWholeApartment={isWholeApartment}
            listingId={listing.id}
            listing={listing}
            propertyFeatures={listing.propertyFeatures}
            propertyFeaturesOldModel={getFeaturesOldModel(listing)}
            roomsInApartment={listing.roomsInApartment}
            locale={locale}
            roomDescription={roomDescription}
            hasRoomDescriptionPricing={hasRoomDescriptionPricing}
          />
        )}

        {(contents.whatWeLiked || contents.thingsToKeepInMind) &&
          !isListingSimplificationBottomVariant && (
            <ListingHighlights
              whatWeLiked={contents.whatWeLiked}
              thingsToKeepInMind={contents.thingsToKeepInMind}
            />
          )}

        {listing.reviews && listing.reviews.count > 0 && (
          <Reviews reference={listing.id} />
        )}

        {isListingSimplificationBottomVariant && (
          <>
            {listing.otherRooms?.length > 0 && (
              <OtherRoomsSection
                otherRooms={listing.otherRooms}
                mainListing={listing}
                locale={locale}
                isLogged={isUserLogged}
              />
            )}
            <MeetYourLandlord landlordScoring={listing.landlordScoring} />
            <HowBookingWorks />
          </>
        )}

        {isRoomsAggregator && (
          <ListingPricing
            cityId={listing.cityId}
            bills={bills}
            contractType={listing.contractType}
            currencyLabel={listing.pricing.currencyLabel}
            pricing={listing.pricing}
            roomDescription={roomDescription}
            listingType={listing.type}
            m2={listing.m2}
            hasDates={!!hasDates}
            titleKey="listing.pricing.title.standalone"
            landlordPolicies={
              moveLandlordPoliciesToPricing &&
              landlordPoliciesHasFees &&
              contents.landlordPolicies
            }
            servicesAndExpenses={{
              periodicCleaning:
                getPeriodicCleaningFromListingCosts(
                  listing.listingCosts || {}
                ) || listing.propertyFeatures?.periodicCleaning
            }}
            isLogged={isUserLogged}
            listingId={listing.id}
            listing={listing}
            moveIn={moveIn}
            moveOut={moveOut}
            deviceType={deviceType}
            hasHiddenFees={listing.hasHiddenFees}
            hasRoomDescriptionPricing={hasRoomDescriptionPricing}
          />
        )}

        {!isListingSimplificationBottomVariant && <CancellationPolicies />}

        {(() => {
          if (isListingSimplificationBottomVariant) {
            return (
              <PropertyRulesSimplified
                propertyRules={listing.listingRules?.propertyRules}
                currency={listing.pricing.currencyLabel}
                contractType={listing.contractType}
                contractualPermissions={
                  listing.listingRules?.contractualPermissions
                }
                documentsPolicy={listing.listingRules?.documentsPolicy}
                minStay={listing.minStay}
                landlordPolicies={landlordPolicies}
                moveInMoveOutPolicy={listing.listingRules?.moveInMoveOutPolicy}
              />
            );
          }

          if (
            listing.listingRules?.propertyRules &&
            listing.listingRules?.documentsPolicy &&
            listing.listingRules?.moveInMoveOutPolicy &&
            listing.listingRules?.contractualPermissions
          ) {
            return (
              <ListingSectionObservable section="landlordRules">
                <ListingTitle role="secondary">
                  {trans('property.rule.main.title')}
                </ListingTitle>
                <PropertyRulesRedesign
                  propertyRules={listing.listingRules?.propertyRules}
                  documentsPolicy={listing.listingRules?.documentsPolicy}
                  moveInMoveOutPolicy={
                    listing.listingRules?.moveInMoveOutPolicy
                  }
                  contractualPermissions={
                    listing.listingRules?.contractualPermissions
                  }
                  currency={listing.pricing.currencyLabel}
                  contractType={listing.contractType}
                  landlordPolicies={landlordPolicies}
                />
              </ListingSectionObservable>
            );
          }

          return (
            <PropertyRules
              landlordPolicies={landlordPolicies}
              landlordRules={listing.landlordRules}
              landlordRulesInfo={landlordRulesInfo}
              cityId={listing.cityId}
              countryCode={listing.countryCode}
            />
          );
        })()}

        {!isListingSimplificationBottomVariant && (
          <>
            <ExtendedLandlordInformation
              currentListingId={listing.id}
              landlordHomecards={landlordHomecards}
              landlordScoring={listing.landlordScoring}
            />
            <ListingHowToBook
              hasVetting={listing?.bookingConditions?.hasVetting}
            />
          </>
        )}

        {isListingPaymentsModuleV2Variant &&
          !isRoomsAggregator &&
          !isListingSimplificationBottomVariant && (
            <ListingPaymentsModule
              contractType={listing.contractType}
              hasDates={hasDates}
              deviceType={deviceType}
            />
          )}

        {!isRoomsAggregator && !isListingSimplificationBottomVariant && (
          <ListingAvailabilities
            availableFrom={listing.availableFromDateIso8601}
            dateUpdated={listing.refreshAvailabilitiesDate}
            unavailabilities={listing.unavailabilities}
            seasonalPrices={listing.pricing.monthlyPrice.pricesByMonth}
            currency={listing.pricing.currencySymbol}
            minStay={listing.minStay}
            maxStay={listing.maxStay}
            bookingTooEarlyDays={listing.stayRules?.bookingTooEarlyDays}
            titleKey={'listing.availabilities.title'}
          />
        )}

        {isListingSimplificationBottomVariant && !isRoomsAggregator ? (
          <ListingLocation listing={listing} bordered={false} />
        ) : (
          !!contents.transportation && (
            <ListingSurroundings
              transportationDescription={contents.transportation}
            />
          )
        )}

        {isListingSimplificationBottomVariant && (
          <ListingSectionObservable section="faqs" bordered={false}>
            <ListingTitle role="secondary">
              {trans('support.faq.title')}
            </ListingTitle>
            <FAQAccordion data={getFaqs()} theme="listing" />
          </ListingSectionObservable>
        )}

        {!!contents.flatmates && (
          <ListingFlatmates flatmatesDescription={contents.flatmates} />
        )}
      </div>
      <BillsIncludedModal
        bills={bills}
        listingType={listing.type}
        currency={listing.pricing.currencySymbol}
      />
    </Fragment>
  );
};

ListingInformation.propTypes = {
  listing: PropTypes.shape(ListingShape).isRequired,
  landlordRulesInfo: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      value: PropTypes.string,
      additionalInfo: PropTypes.string
    })
  }).isRequired,
  onLanguageChange: PropTypes.func.isRequired,
  showTranslatedContent: PropTypes.bool.isRequired,
  mustShowLanguageToggle: PropTypes.bool.isRequired,
  isRoomsAggregator: PropTypes.bool.isRequired,
  isWholeApartment: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  deviceType: PropTypes.string.isRequired,
  landlordHomecards: PropTypes.arrayOf(PropTypes.object),
  moveIn: PropTypes.string,
  moveOut: PropTypes.string
};

export default ListingInformation;
